import { computed, onMounted, onBeforeUnmount } from 'vue';

/**
 * Keyboard navigation for the levelling-evaluation question steps (PD-1239) —
 * shared between rm-portal and rm-portal-beta.
 *
 * Track / Zone / SubFactor / Classification / Guided are structurally identical —
 * one option list plus Previous/Next — so the interaction is defined once here
 * rather than reimplemented per step and per portal:
 *
 *   • number keys 0-9  → select the option whose label number matches the key
 *   • ArrowLeft        → previous question (emit 'back')
 *   • ArrowRight       → next question (submit the answer, if allowed)
 *
 * The digit is SEMANTIC — it matches what the user can see on the option, not an
 * internal index: a sub-factor keys on the level's score ("press 4 for a 4-point
 * level"), classification/zone key on the digits in the level abbreviation
 * ("IC5" → 5), and plain option lists key on 1-based position. Callers own that
 * mapping via `options()`.
 *
 * The listener is on `document` so it responds wherever focus happens to be —
 * except while the user is typing (rationale textarea, inputs, contenteditable),
 * where the keys must keep their normal meaning.
 *
 * IMPORTANT — `isActive`. rm-portal gives each question step its own component, so
 * only one instance is ever mounted. rm-portal-beta renders every step as a v-if
 * branch of ONE component, so all of its instances are created together and stay
 * alive for the whole page. Without a guard they all listen at once, the
 * first-registered one calls preventDefault(), and every later one bails on
 * `defaultPrevented` — so one step silently swallows the keys for all the others.
 * Any host with more than one instance alive MUST pass `isActive`.
 *
 * @param {Object}   opts
 * @param {Function} opts.options       () => [{ key: <digit>, value }] in display order
 * @param {Function} opts.onSelect      (value) => select that option
 * @param {Function} opts.onNext        () => advance / submit
 * @param {Function} opts.onPrev        () => go back
 * @param {Function} [opts.canNext]     () => is advancing allowed (mirrors Next's disabled state)
 * @param {Function} [opts.canNavigate] () => are the arrows active at all (e.g. off while
 *                                      editing a single answer from the summary)
 * @param {string}   [opts.verb]        word used in the footer hint ("score" / "select")
 * @param {Function} [opts.isActive]    () => is THIS instance the live step. Defaults to
 *                                      always active (correct when only one instance
 *                                      exists, as in rm-portal).
 * @param {string}   [opts.overlaySelector] CSS selector for "a modal is open over this
 *                                      step, so keys shouldn't reach it". Portal-specific:
 *                                      rm-portal passes its Vuetify overlay classes; beta
 *                                      passes its own scrim class. Defaults to both.
 * @returns {{ keyHint: import('vue').ComputedRef<string> }}
 */
export const DEFAULT_OVERLAY_SELECTOR =
  '.v-dialog--active, .v-menu__content--active, .v-overlay--active, .ev-modal-scrim, .lvl-modal-scrim';

export function questionKeyboardNav({
  options,
  onSelect,
  onNext,
  onPrev,
  canNext = () => true,
  canNavigate = () => true,
  verb = 'select',
  overlaySelector = DEFAULT_OVERLAY_SELECTOR,
  isActive = () => true,
}) {
  function isTypingTarget(el) {
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  // A dialog/menu (Restart confirm, level explanation, …) can be open over the
  // question step; the keys shouldn't reach the question behind it.
  function overlayOpen() {
    if (!overlaySelector) return false;
    return !!document.querySelector(overlaySelector);
  }

  const pressableKeys = computed(() =>
    (options() || []).map((o) => o.key).filter((k) => Number.isInteger(k) && k >= 0 && k <= 9),
  );

  const keyHint = computed(() => {
    if (!isActive()) return '';
    const keys = pressableKeys.value;
    if (!keys.length) return '';
    const min = Math.min(...keys);
    const max = Math.max(...keys);
    const range = min === max ? `${min}` : `${min}–${max}`;
    return `Keys ${range} to ${verb} · ← / → to move`;
  });

  function handleKeydown(event) {
    // Not the live step → this instance must not see the event at all, or it would
    // claim keys meant for the step actually on screen.
    if (!isActive()) return;
    // Don't fight the browser when a shortcut/modifier is in play, when another
    // handler already claimed the event, or while the user is typing / an overlay
    // is open.
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    if (isTypingTarget(event.target) || overlayOpen()) return;

    if (event.key >= '0' && event.key <= '9') {
      const digit = Number(event.key);
      const match = (options() || []).find((o) => o.key === digit);
      if (match) {
        event.preventDefault();
        onSelect(match.value);
      }
      return;
    }

    if (!canNavigate()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onPrev();
    } else if (event.key === 'ArrowRight') {
      if (canNext()) {
        event.preventDefault();
        onNext();
      }
    }
  }

  onMounted(() => document.addEventListener('keydown', handleKeydown));
  onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown));

  return { keyHint };
}

/**
 * Digits from a level abbreviation, e.g. "IC5" → 5. Classification and zone steps
 * key on this so the key pressed matches the label shown (PD-1239 commit
 * 5b7a5059c fixed these steps to use the abbreviation rather than the internal
 * level index).
 */
export function abbreviationKey(abbreviation) {
  const m = String(abbreviation || '').match(/\d+/);
  return m ? Number(m[0]) : null;
}
